<template>
  <Dialog :open="open" @update:open="handleClose" :title="dialogTitle" size="lg">
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <div class="space-y-4">
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Connection Details</h4>
          <div class="space-y-4">
            <!-- Name Input -->
            <div>
              <label for="conn-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Name <span class="text-red-500">*</span>
              </label>
              <Input
                id="conn-name"
                v-model="formState.name"
                placeholder="e.g., My Groq Connection"
                :disabled="isSubmitting"
                :error="errors.name"
                class="w-full"
              />
              <p v-if="errors.name" class="mt-1.5 text-xs text-red-600 dark:text-red-400">{{ errors.name }}</p>
            </div>

            <!-- Slug Input -->
            <div>
              <label for="conn-slug" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Slug <span class="text-red-500">*</span>
              </label>
              <Input
                id="conn-slug"
                v-model="formState.slug"
                placeholder="e.g., my-groq-conn"
                :disabled="isSubmitting || isEditMode"
                :error="errors.slug"
                class="w-full"
              />
              <p v-if="errors.slug" class="mt-1.5 text-xs text-red-600 dark:text-red-400">{{ errors.slug }}</p>
            </div>

            <!-- SDK Type Select -->
            <div>
              <label for="conn-sdk-type" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SDK Type <span class="text-red-500">*</span>
              </label>
              <select
                id="conn-sdk-type"
                v-model="formState.sdkType"
                class="w-full h-10 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:border-transparent text-sm text-gray-900 dark:text-white"
                :disabled="isSubmitting || isEditMode"
              >
                <option value="openai_compatible">OpenAI Compatible</option>
                <option value="anthropic">Anthropic</option>
                <option value="bedrock">AWS Bedrock</option>
                <option value="google">Google AI Studio</option>
                <option value="vertex_ai">Google Vertex AI</option>
                <option value="groq">Groq</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Settings</h4>
          <div class="space-y-4">
            <!-- Base URL Input -->
            <div v-if="formState.sdkType === 'openai_compatible'">
              <label for="conn-base-url" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Base URL <span class="text-red-500">*</span>
              </label>
              <Input
                id="conn-base-url"
                v-model="formState.baseUrl"
                type="url"
                placeholder="https://api.example.com/v1"
                :disabled="isSubmitting"
                :error="errors.baseUrl"
                class="w-full"
              />
            </div>

            <!-- API Key Input -->
            <div>
              <label for="conn-api-key" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                API Key <span v-if="!isEditMode" class="text-red-500">*</span>
              </label>
              <Input
                id="conn-api-key"
                v-model="formState.apiKey"
                type="password"
                :placeholder="isEditMode ? 'Leave empty to keep existing key' : 'Enter API key'"
                :disabled="isSubmitting"
                :error="errors.apiKey"
                class="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" @click="handleClose" :disabled="isSubmitting">Cancel</Button>
        <Button type="submit" variant="primary" :loading="isSubmitting">
          {{ isEditMode ? 'Update Connection' : 'Create Connection' }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Dialog, Button, Input } from '@asabdev/asab-ui';
  import { validateName, validateSlug, validateUrl } from '../utils/validation';
  import type { CustomModelConnectionResponse, SdkType } from '../types';

  interface Props {
    open: boolean;
    editConnection?: CustomModelConnectionResponse | null;
  }

  const props = withDefaults(defineProps<Props>(), {
    editConnection: null,
  });

  const emit = defineEmits(['update:open', 'created', 'updated', 'cancel']);

  const formState = ref({
    name: '',
    slug: '',
    sdkType: 'openai_compatible' as SdkType,
    baseUrl: '',
    apiKey: '',
  });

  const isSubmitting = ref(false);
  const errors = ref<Record<string, string>>({});
  const isEditMode = computed(() => !!props.editConnection);
  const dialogTitle = computed(() => isEditMode.value ? 'Edit Connection' : 'Create Connection');

  const validateForm = () => {
    errors.value = {};
    if (!validateName(formState.value.name).isValid) errors.value.name = 'Invalid name';
    if (!isEditMode.value && !validateSlug(formState.value.slug).isValid) errors.value.slug = 'Invalid slug';
    if (formState.value.sdkType === 'openai_compatible' && !validateUrl(formState.value.baseUrl).isValid) errors.value.baseUrl = 'Invalid URL';
    if (!isEditMode.value && !formState.value.apiKey.trim()) errors.value.apiKey = 'API key required';
    return Object.keys(errors.value).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    isSubmitting.value = true;
    const data = { ...formState.value };
    if (isEditMode.value) {
      emit('updated', { name: data.name, base_url: data.baseUrl, api_key: data.apiKey });
    } else {
      // API contract is snake_case (CreateConnectionRequest). formState is
      // camelCase; map it explicitly — emits are untyped so a raw spread
      // silently sent sdkType/baseUrl/apiKey and the gateway 400'd on the
      // missing sdk_type.
      emit('created', {
        name: data.name,
        slug: data.slug,
        sdk_type: data.sdkType,
        base_url: data.sdkType === 'openai_compatible' ? data.baseUrl : undefined,
        api_key: data.apiKey,
      });
    }
    setTimeout(() => { isSubmitting.value = false; }, 500);
  };

  const handleClose = () => { emit('update:open', false); emit('cancel'); };

  watch(() => props.open, (val) => {
    if (val && props.editConnection) {
      formState.value = {
        name: props.editConnection.name,
        slug: props.editConnection.slug,
        sdkType: props.editConnection.sdk_type,
        baseUrl: props.editConnection.base_url || '',
        apiKey: '',
      };
    } else {
      formState.value = { name: '', slug: '', sdkType: 'openai_compatible', baseUrl: '', apiKey: '' };
    }
  }, { immediate: true });
</script>
